import React from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
    Button,
    Paper,
    Box,
    Divider,
    Typography,
    CircularProgress,
} from '@mui/material';
import { useQuery } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import { getSingleBroadcastMessage } from 'core/API/broadcast';
import { formatDateMMDDYYYY, formatDateTime12Hour } from 'shared/utils/dateUtils';
import { usePermissionCheck } from 'core/hooks/usePermissionCheck';

type RouteParams = { id?: string };

const ViewBroadcast: React.FC = () => {
    const history = useHistory();
    const { id } = useParams<RouteParams>();

    // Broadcast permission checks (module-level, like Logbook)
    const { hasModuleViewPermission } = usePermissionCheck();
    const canViewBroadcastMy = hasModuleViewPermission('Broadcast - My');
    const canViewBroadcastAll = hasModuleViewPermission('Broadcast - All');
    const canViewBroadcastOrganization = hasModuleViewPermission('Broadcast - Organization');
    const canViewBroadcast = canViewBroadcastMy || canViewBroadcastAll || canViewBroadcastOrganization;

    // Fetch broadcast data
    const { data: broadcastData, isLoading, error } = useQuery(
        ['broadcast', id],
        () => getSingleBroadcastMessage(Number(id)),
        {
            enabled: !!id,
        }
    );

    const handleBackClick = () => {
        history.push(ROUTES.BROADCAST);
    };

    // Helper function to get receiver name
    const getReceiverName = (): string => {
        if (!broadcastData) return 'N/A';
        
        if (broadcastData.receiverUser) {
            const user = broadcastData.receiverUser;
            return `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'N/A';
        } else if (broadcastData.receiverGroup) {
            const group = broadcastData.receiverGroup;
            return group.groupName || `Group ${group.id}` || 'N/A';
        }
        return 'N/A';
    };

    // Helper function to format date and time as MM/DD/YYYY HH:MM:SS AM/PM
    const formatDateTimeFromAPI = (createdDate: string | null | undefined, createdTime: string | null | undefined): string => {
        if (createdDate && createdTime) {
            return formatDateTime12Hour(createdDate, createdTime);
        } else if (createdDate) {
            return formatDateTime12Hour(createdDate, null);
        }
        return 'N/A';
    };

    // Helper function to get priority label
    const getPriorityLabel = (): string => {
        if (!broadcastData?.priority) return 'N/A';
        
        if (typeof broadcastData.priority === 'string') {
            return broadcastData.priority.split('/').pop() || 'N/A';
        }
        return broadcastData.priority.label || broadcastData.priority.name || 'N/A';
    };

    // Helper function to get sender name
    const getSenderName = (): string => {
        if (!broadcastData?.sender) return 'N/A';
        const sender = broadcastData.sender;
        return `${sender.firstName || ''} ${sender.lastName || ''}`.trim() || 'N/A';
    };

    // Loading state
    // Check permissions before loading
    if (!canViewBroadcast) {
        return (
            <Paper elevation={1} style={{ padding: '24px', maxWidth: '100%', margin: '0 auto' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Button
                        style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white' }}
                        onClick={handleBackClick}
                    >
                        Back
                    </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <Typography variant="h6" color="error">
                        You do not have permissions to view broadcasts.
                    </Typography>
                </Box>
            </Paper>
        );
    }

    if (isLoading) {
        return (
            <Paper elevation={1} style={{ padding: '24px', maxWidth: '100%', margin: '0 auto' }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </Paper>
        );
    }

    // Error state
    if (error || !broadcastData) {
        return (
            <Paper elevation={1} style={{ padding: '24px', maxWidth: '100%', margin: '0 auto' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Button
                        style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white' }}
                        onClick={handleBackClick}
                    >
                        Back
                    </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <Typography color="error">
                        {error ? 'Error loading broadcast' : 'Broadcast not found'}
                    </Typography>
                </Box>
            </Paper>
        );
    }

    return (
        <Paper elevation={1} style={{ padding: '24px', maxWidth: '100%', margin: '0 auto' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Button
                    style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white' }}
                    onClick={handleBackClick}
                >
                    Back
                </Button>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Email-like View Mode */}
            <Box
                sx={{
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    padding: '20px',
                    maxWidth: '100%',
                }}
            >
                {/* Subject - Large Bold */}
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 'bold',
                        fontSize: '1.5rem',
                        mb: 3,
                        color: '#333',
                    }}
                >
                    {broadcastData.title || 'No Subject'}
                </Typography>

                {/* Header Info */}
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 3,
                        mb: 3,
                        pb: 2,
                        borderBottom: '1px solid #eee',
                    }}
                >
                    <Box>
                        <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
                            From:
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#333' }}>
                            {getSenderName()}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
                            To:
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#333' }}>
                            {getReceiverName()}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
                            Priority:
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#333' }}>
                            {getPriorityLabel()}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
                            Date & Time:
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#333' }}>
                            {formatDateTimeFromAPI(
                                broadcastData.createdDate || broadcastData.createdAt || broadcastData.created_at,
                                broadcastData.createdTime
                            )}
                        </Typography>
                    </Box>
                </Box>

                {/* Content Area */}
                <Box
                    sx={{
                        mt: 3,
                        '& .ql-editor': {
                            padding: 0,
                            fontSize: '1rem',
                            fontFamily: 'inherit',
                            minHeight: 'auto',
                        },
                        '& .ql-container': {
                            border: 'none',
                            fontSize: '1rem',
                            fontFamily: 'inherit',
                        },
                        '& .ql-toolbar': {
                            display: 'none',
                        },
                        '& img': {
                            maxWidth: '100%',
                            height: 'auto',
                            margin: '10px 0',
                        },
                        '& a': {
                            color: '#1976d2',
                            textDecoration: 'underline',
                        },
                    }}
                >
                    <ReactQuill
                        value={broadcastData.content || ''}
                        readOnly={true}
                        theme="snow"
                        modules={{ toolbar: false }}
                    />
                </Box>
            </Box>
        </Paper>
    );
};

export default ViewBroadcast;

